import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Copy, Check, Code2 } from 'lucide-react';
import axios from 'axios';

const JsonFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [indent, setIndent] = useState(2);
  const [sortKeys, setSortKeys] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const formatJson = async () => {
    if (!input.trim()) {
      setError('Please enter JSON data');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/json/format`,
        {
          json_text: input,
          indent,
          sort_keys: sortKeys
        }
      );

      setOutput(response.data.formatted);
      setIsValid(response.data.is_valid);
      setStats(response.data.stats);
      
      if (!response.data.is_valid) {
        setError(response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to format JSON');
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const minifyJson = () => {
    setIndent(0);
    setTimeout(formatJson, 100);
  };

  const prettifyJson = () => {
    setIndent(2);
    setTimeout(formatJson, 100);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">JSON Formatter & Validator</h1>
        <p className="text-muted-foreground">
          Format, validate, and beautify JSON data
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Input JSON</CardTitle>
            <CardDescription>Paste your JSON data here</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder='{"name": "John", "age": 30}'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={16}
              className="font-mono text-sm"
            />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="sort-keys">Sort Keys Alphabetically</Label>
                <Switch
                  id="sort-keys"
                  checked={sortKeys}
                  onCheckedChange={setSortKeys}
                />
              </div>

              <div>
                <Label>Indentation: {indent === 0 ? 'Minified' : `${indent} spaces`}</Label>
                <div className="flex gap-2 mt-2">
                  <Button onClick={minifyJson} variant="outline" size="sm">
                    Minify
                  </Button>
                  <Button onClick={() => setIndent(2)} variant="outline" size="sm">
                    2 Spaces
                  </Button>
                  <Button onClick={() => setIndent(4)} variant="outline" size="sm">
                    4 Spaces
                  </Button>
                </div>
              </div>
            </div>

            <Button onClick={formatJson} disabled={loading} className="w-full" size="lg">
              <Code2 className="mr-2 h-5 w-5" />
              {loading ? 'Formatting...' : 'Format JSON'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Formatted Output</CardTitle>
                <CardDescription>
                  {isValid ? 'Valid JSON' : 'Invalid JSON'}
                </CardDescription>
              </div>
              {output && isValid && (
                <Button onClick={copyToClipboard} variant="outline" size="sm">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={output}
              readOnly
              rows={16}
              className="font-mono text-sm"
              placeholder="Formatted JSON will appear here"
            />

            {error && (
              <Alert variant="destructive">
                <AlertDescription className="font-mono text-xs whitespace-pre-wrap">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {stats && isValid && (
              <div className="bg-muted p-4 rounded-lg text-sm space-y-1">
                <p><span className="font-semibold">Size:</span> {stats.size} characters</p>
                <p><span className="font-semibold">Lines:</span> {stats.lines}</p>
                <p><span className="font-semibold">Objects:</span> {stats.objects}</p>
                <p><span className="font-semibold">Arrays:</span> {stats.arrays}</p>
                <p><span className="font-semibold">Keys:</span> {stats.keys}</p>
                <p><span className="font-semibold">Max Depth:</span> {stats.max_depth}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Features</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <h4 className="font-semibold mb-1">Validation</h4>
            <p className="text-muted-foreground">Instantly validates JSON syntax with detailed error messages</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Formatting</h4>
            <p className="text-muted-foreground">Beautify or minify with custom indentation</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Statistics</h4>
            <p className="text-muted-foreground">View structure analysis and complexity metrics</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JsonFormatter;
